/**
 * JSON Formatters
 *
 * Utilities for formatting and displaying JSON/JSONB values in MetadataTable.
 * Used to provide compact previews with click-to-expand functionality.
 */

import type { FieldMetadata } from '@/services/metadataService'

/**
 * Format JSON value for compact display in table cells.
 *
 * @param value - The raw value (could be object, array, or primitive)
 * @returns Compact string representation (e.g., "{3 keys}" or "[5 items]")
 *
 * @example
 * formatJsonPreview({ a: 1, b: 2 }) // "{2 keys}"
 * formatJsonPreview([1, 2, 3]) // "[3 items]"
 * formatJsonPreview(null) // "-"
 */
export function formatJsonPreview(value: unknown): string {
  if (value === null || value === undefined) {
    return '-'
  }

  if (Array.isArray(value)) {
    const count = value.length
    return `[${count} item${count !== 1 ? 's' : ''}]`
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value)
    const count = keys.length
    return `{${count} key${count !== 1 ? 's' : ''}}`
  }

  // Not a JSON object/array - return as string
  return String(value)
}

/**
 * Check if a value is a JSON object or array (not a primitive).
 *
 * @param value - The value to check
 * @returns True if value is an object or array (excluding null)
 *
 * @example
 * isJsonValue({ a: 1 }) // true
 * isJsonValue([1, 2]) // true
 * isJsonValue(null) // false
 * isJsonValue("string") // false
 */
export function isJsonValue(value: unknown): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === 'object'
}

/**
 * Check if field metadata indicates a JSON/JSONB type.
 *
 * @param metadata - Field metadata from backend schema
 * @returns True if py_type is 'json' or 'jsonb'
 *
 * @example
 * isJsonField({ py_type: 'json' }) // true
 * isJsonField({ py_type: 'jsonb' }) // true
 * isJsonField({ py_type: 'string' }) // false
 */
export function isJsonField(metadata: FieldMetadata | undefined): boolean {
  if (!metadata) return false
  const pyType = (metadata.py_type || '').toLowerCase()
  return pyType === 'json' || pyType === 'jsonb'
}
